import { BackendApiClient } from './backend-client';
import type { ScopeContext } from '../model/types';
import { createResetSessionAuthHeaders } from './reset-session';
import type { PrivateKey as IPrivateKey } from 'starkbank-ecdsa';
import {
  BackendValidationError,
  backendValidationErrorFrom,
  type BackendError,
  type BackendErrorResponse,
} from '../../../shared/lib/backend-errors';
import { requireList, UNREADABLE_RESPONSE_CODE } from '@/shared/lib/response-shape';

export { BackendValidationError };
export type { BackendError, BackendErrorResponse };

const backendClient = new BackendApiClient();

export interface UserInviteRequest {
  invites: Array<{
    user_email: string;
    role_ids: string[];
  }>;
}

export interface UserInviteResponse {
  message?: string;
  [key: string]: any; // Allow for additional properties
}

export interface InviteUser {
  id: string;
  name: string;
  type: string;
  // The account cell serializes this as `reference_id or ""` on tst/sbx, and omits the key
  // entirely on prd: it has not yet promoted VEL-417, so its User model still has no
  // reference_id attribute at all — the key is genuinely absent there, not merely null. The
  // render path falls back rather than assume the key is present and non-empty.
  reference_id?: string | null;
  // Legacy identity key, still sent by prd until VEL-417 promotes there. Kept only as a
  // fallback for that promotion-lag window — reference_id is the field going forward.
  email?: string | null;
  tax_id?: string;
  allowed_ips?: string[];
  created: string;
}

/**
 * Read the `users` array off an invite-user response and check the fields the confirmation
 * screen dereferences while rendering. `requireList` proves the value is an array and then
 * casts its elements, so a row missing `id`/`name`/`type` would pass the fetch and throw
 * inside the render tree instead — outside any try, with no error boundary above it.
 * `reference_id` and `email` are excluded from the check: both are documented-nullable, and
 * the render path already falls back when they are absent.
 */
export function requireInviteUserList(body: unknown): InviteUser[] {
  const users = requireList<InviteUser>(body, 'users', 'Unreadable invite user response');

  const malformed = users.some(user =>
    !user
    || typeof user.id !== 'string'
    || typeof user.name !== 'string'
    || typeof user.type !== 'string'
  );

  if (malformed) {
    throw new BackendValidationError('Unreadable invite user response', UNREADABLE_RESPONSE_CODE);
  }

  return users;
}

export interface InviteUserResponse {
  users: InviteUser[];
  cursor: string;
}

export interface UpdateInviteRequest {
  invite: {
    status: 'processing' | 'canceled';
  };
}

export interface CreateInviteUserRequest {
  users: Array<{
    name: string;
    tax_id: string;
    public_key: string;
  }>;
}

export interface InviteDetails {
  id: string;
  status: string;
  email: string;
  scope_type?: string;
  scope_id?: string;
  role_ids?: string[];
  expiration?: string;
  created?: string;
  [key: string]: any;
}

export interface InviteDetailsResponse {
  invite?: InviteDetails;
  [key: string]: any;
}

export interface InviteScope {
  type: string;
  name: string;
  id: string;
}

export interface InviteScopeResponse {
  scopes: InviteScope[];
}

/**
 * Get invite details (including email)
 * Uses reset session authentication (from email magic link)
 */
export async function getInviteDetails(
  inviteId: string,
  sessionId: string,
  sessionPrivateKey: IPrivateKey
): Promise<InviteDetailsResponse> {
  try {
    const headers = await createResetSessionAuthHeaders(sessionId, sessionPrivateKey);

    const response = await fetch(`/api/user-invite/${inviteId}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      let errorData: BackendErrorResponse;
      try {
        errorData = await response.json();
      } catch {
        throw new BackendValidationError(`HTTP ${response.status}: ${response.statusText}`);
      }
      throw backendValidationErrorFrom(errorData, `HTTP ${response.status}: ${response.statusText}`);
    }

    const responseData = await response.json();
    return responseData as InviteDetailsResponse;
  } catch (error) {
    // Re-throw the error with its original message (which should now contain the backend message)
    throw error;
  }
}

/**
 * Get friendly invite scope (type + name + id) for the acceptance page.
 * Uses reset session authentication (from email magic link).
 */
export async function getInviteScope(
  inviteId: string,
  sessionId: string,
  sessionPrivateKey: IPrivateKey
): Promise<InviteScopeResponse> {
  const headers = await createResetSessionAuthHeaders(sessionId, sessionPrivateKey);

  const response = await fetch(`/api/user-invite/${inviteId}/scope`, {
    method: 'GET',
    headers
  });

  if (!response.ok) {
    let errorData: BackendErrorResponse;
    try {
      errorData = await response.json();
    } catch {
      throw new BackendValidationError(`HTTP ${response.status}: ${response.statusText}`);
    }
    throw backendValidationErrorFrom(errorData, `HTTP ${response.status}: ${response.statusText}`);
  }

  const responseData = await response.json();
  return responseData as InviteScopeResponse;
}

/**
 * Send user invitation(s)
 * @param sessionId - The session ID for authentication
 * @param invites - Array of invite objects with user_email and role_ids
 * @param scopeContext - Optional scope context (account or holder)
 * @returns Promise<UserInviteResponse> - The response from the backend
 */
export async function sendUserInvite(
  sessionId: string,
  invites: UserInviteRequest['invites'],
  scopeContext?: ScopeContext
): Promise<UserInviteResponse> {
  try {
    const requestBody: UserInviteRequest = {
      invites
    };

    const response = await backendClient.post('/user-invite', sessionId, requestBody, scopeContext);
    
    if (response.success && response.data) {
      return response.data as UserInviteResponse;
    } else {
      throw new Error(response.error || 'Failed to send user invite');
    }
  } catch (error) {
    throw new Error(`Failed to send user invite: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Get user associated with an invite
 * Uses reset session authentication (from email magic link)
 */
export async function getInviteUser(
  inviteId: string,
  sessionId: string,
  sessionPrivateKey: IPrivateKey
): Promise<InviteUserResponse> {
  try {
    const headers = await createResetSessionAuthHeaders(sessionId, sessionPrivateKey);

    const response = await fetch(`/api/user-invite/${inviteId}/user`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      let errorData: BackendErrorResponse;
      try {
        errorData = await response.json();
      } catch {
        throw new BackendValidationError(`HTTP ${response.status}: ${response.statusText}`);
      }
      throw backendValidationErrorFrom(errorData, `HTTP ${response.status}: ${response.statusText}`);
    }

    const responseData = await response.json();
    const nextCursor = (responseData as { cursor?: unknown })?.cursor;
    return {
      users: requireInviteUserList(responseData),
      cursor: typeof nextCursor === 'string' ? nextCursor : '',
    };
  } catch (error) {
    if (error instanceof BackendValidationError) {
      throw error;
    }
    throw new BackendValidationError(`Failed to get invite user: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Update invite status (accept or deny)
 * Uses reset session authentication (from email magic link)
 */
export async function updateInviteStatus(
  inviteId: string,
  sessionId: string,
  sessionPrivateKey: IPrivateKey,
  status: 'processing' | 'canceled'
): Promise<UserInviteResponse> {
  try {
    const requestBody: UpdateInviteRequest = {
      invite: { status }
    };

    const headers = await createResetSessionAuthHeaders(sessionId, sessionPrivateKey, requestBody);

    const response = await fetch(`/api/user-invite/${inviteId}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      let errorData: BackendErrorResponse;
      try {
        errorData = await response.json();
      } catch {
        throw new BackendValidationError(`HTTP ${response.status}: ${response.statusText}`);
      }
      throw backendValidationErrorFrom(errorData, `HTTP ${response.status}: ${response.statusText}`);
    }

    const responseData = await response.json();
    return responseData as UserInviteResponse;
  } catch (error) {
    if (error instanceof BackendValidationError) {
      throw error;
    }
    throw new BackendValidationError(`Failed to update invite status: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Create user for an invite
 * Uses reset session authentication (from email magic link)
 */
export async function createInviteUser(
  inviteId: string,
  sessionId: string,
  sessionPrivateKey: IPrivateKey,
  userData: CreateInviteUserRequest['users'][0]
): Promise<UserInviteResponse> {
  try {
    const requestBody: CreateInviteUserRequest = {
      users: [userData]
    };

    const headers = await createResetSessionAuthHeaders(sessionId, sessionPrivateKey, requestBody);

    const response = await fetch(`/api/user-invite/${inviteId}/user`, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      let errorData: BackendErrorResponse;
      try {
        errorData = await response.json();
      } catch {
        throw new BackendValidationError(`HTTP ${response.status}: ${response.statusText}`);
      }
      throw backendValidationErrorFrom(errorData, `HTTP ${response.status}: ${response.statusText}`);
    }

    const responseData = await response.json();
    return responseData as UserInviteResponse;
  } catch (error) {
    if (error instanceof BackendValidationError) {
      throw error;
    }
    throw new BackendValidationError(`Failed to create invite user: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

